/**
 * Typed client for /api/v1/sysadmin/*. Uses its own localStorage key so it
 * never collides with the parent session.
 */
import { DEFAULT_API_BASE } from '@xiaopeiban/shared';
import type { ApiError } from '@xiaopeiban/shared';
import { ApiCallError } from './client';

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || DEFAULT_API_BASE;

const SYSADMIN_TOKEN_KEY = 'sysadmin_token';

export function getSysToken(): string | null {
  return localStorage.getItem(SYSADMIN_TOKEN_KEY);
}
export function setSysToken(token: string): void {
  localStorage.setItem(SYSADMIN_TOKEN_KEY, token);
}
export function clearSysToken(): void {
  localStorage.removeItem(SYSADMIN_TOKEN_KEY);
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getSysToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || (data as ApiError).error) {
    const e = (data as ApiError).error;
    throw new ApiCallError(
      e?.code || 'UNKNOWN',
      e?.message || `HTTP ${res.status}`,
      res.status,
      e?.detail,
    );
  }
  return data as T;
}

export interface SysParent {
  id: number;
  email: string;
  email_verified: number;
  display_name: string | null;
  phone: string | null;
  created_at: number;
  last_login: number | null;
  deleted_at: number | null;
  has_password: number;
}

export interface SysChild {
  id: number;
  name: string;
  birth_date: string | null;
  avatar: string | null;
  created_at: number;
  last_login: number | null;
  deleted_at: number | null;
  parent_id: number | null;
  parent_email: string | null;
}

export interface SysAssetFile {
  url: string;
  kind: 'image' | 'sound' | 'model' | 'other';
  size: number;
  group: string;
}

export interface SysAssetCategory {
  key: string;
  label: string;
  dir: string;
  files: SysAssetFile[];
}

export const sysApi = {
  login(username: string, password: string) {
    return call<{ ok: true; token: string; expires_at: number }>(
      '/api/v1/sysadmin/login',
      { method: 'POST', body: JSON.stringify({ username, password }) },
    );
  },
  logout() {
    return call<{ ok: true }>('/api/v1/sysadmin/logout', { method: 'POST' });
  },
  me() {
    return call<{ ok: true; user: string }>('/api/v1/sysadmin/me');
  },
  listUsers() {
    return call<{ parents: SysParent[]; children: SysChild[] }>(
      '/api/v1/sysadmin/users',
    );
  },
  resetPassword(type: 'parent' | 'child', id: number, password: string) {
    return call<{ ok: true }>(
      `/api/v1/sysadmin/users/${type}/${id}/reset-password`,
      { method: 'POST', body: JSON.stringify({ password }) },
    );
  },
  listAssets() {
    return call<{
      base: string;
      generated_at: string;
      categories: SysAssetCategory[];
    }>('/api/v1/sysadmin/assets');
  },
};
